import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { trackEvent } from '@/utils/analytics';
import { toMultiValueItems, type MultiValueItem, type MultiValueSeparator } from './multiValueItems';
import './MultiValueCell.css';

const VISIBLE_LIMIT = 2;
const PANEL_MARGIN = 8;

export type MultiValueTone = 'label' | 'identifier';

type MultiValueCellProps = {
  values?: unknown;
  items?: MultiValueItem[];
  columnLabel?: string;
  tone?: MultiValueTone;
  separator?: MultiValueSeparator;
};

function dedupeItems(items: MultiValueItem[]): MultiValueItem[] {
  const seen = new Set<string>();
  return items.filter(item => {
    const label = item.label.trim();
    if (!label || label === '-' || seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function ValueNode({ item, className }: { item: MultiValueItem; className?: string }) {
  if (item.href) {
    return (
      <a href={item.href} className={className} title={item.label} target="_blank" rel="noopener noreferrer">
        {item.label}
      </a>
    );
  }

  if (item.onClick) {
    return (
      <button type="button" className={className} title={item.label} onClick={item.onClick}>
        {item.label}
      </button>
    );
  }

  return (
    <span className={className} title={item.label}>
      {item.label}
    </span>
  );
}

export function MultiValueCell({
  values,
  items,
  columnLabel,
  tone = 'label',
  separator = '|',
}: MultiValueCellProps) {
  const resolved = dedupeItems(items ?? toMultiValueItems(values, separator));
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();

  const placePanel = useCallback(() => {
    const anchor = triggerRef.current;
    const panel = panelRef.current;
    if (!anchor || !panel) return;

    const rect = anchor.getBoundingClientRect();
    if (rect.bottom < 0 || rect.top > window.innerHeight) {
      setOpen(false);
      return;
    }

    const panelRect = panel.getBoundingClientRect();
    let top = rect.bottom + PANEL_MARGIN;
    if (
      top + panelRect.height > window.innerHeight - PANEL_MARGIN &&
      rect.top - panelRect.height - PANEL_MARGIN > PANEL_MARGIN
    ) {
      top = rect.top - panelRect.height - PANEL_MARGIN;
    }

    let left = rect.left;
    if (left + panelRect.width > window.innerWidth - PANEL_MARGIN) {
      left = window.innerWidth - panelRect.width - PANEL_MARGIN;
    }

    setCoords({ top, left: Math.max(PANEL_MARGIN, left) });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setCoords(null);
      return;
    }

    placePanel();
    window.addEventListener('resize', placePanel);
    window.addEventListener('scroll', placePanel, true);
    return () => {
      window.removeEventListener('resize', placePanel);
      window.removeEventListener('scroll', placePanel, true);
    };
  }, [open, placePanel, resolved.length]);

  useEffect(() => {
    if (!open) return;

    panelRef.current?.focus();

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      setOpen(false);
      triggerRef.current?.focus();
    };

    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (resolved.length === 0) return <span>-</span>;

  const visible = resolved.slice(0, VISIBLE_LIMIT);
  const hiddenCount = resolved.length - visible.length;

  const toggle = () => {
    setOpen(current => {
      const next = !current;
      if (next) {
        trackEvent({
          name: 'multi_value_expand',
          buttonId: 'table_multi_value_expand',
          label: columnLabel || 'multi_value',
          page: 'search_results',
          section: 'results_table',
          feature: 'multi_value_cell',
          eventCategory: 'search',
          additionalParams: { value_count: resolved.length },
        });
      }
      return next;
    });
  };

  const panel =
    open && typeof document !== 'undefined'
      ? createPortal(
          <div
            ref={panelRef}
            id={panelId}
            role="dialog"
            tabIndex={-1}
            aria-label={columnLabel ? `All ${columnLabel}` : 'All values'}
            className="multi-value-cell__panel"
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              visibility: coords ? 'visible' : 'hidden',
            }}
            onPointerDown={event => event.stopPropagation()}
          >
            <p className="multi-value-cell__caption">
              {columnLabel ? `${columnLabel} · ${resolved.length}` : `${resolved.length} values`}
            </p>
            <ul className="multi-value-cell__list">
              {resolved.map(item => (
                <li key={item.id}>
                  <ValueNode
                    item={{
                      ...item,
                      onClick: item.onClick
                        ? () => {
                            item.onClick?.();
                            setOpen(false);
                          }
                        : undefined,
                    }}
                    className="multi-value-cell__option"
                  />
                </li>
              ))}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <span className={`multi-value-cell multi-value-cell--${tone}`}>
      {visible.map(item => (
        <ValueNode key={item.id} item={item} className="multi-value-cell__chip" />
      ))}
      {hiddenCount > 0 && (
        <button
          ref={triggerRef}
          type="button"
          className="multi-value-cell__more"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={panelId}
          aria-label={`Show ${hiddenCount} more values`}
          onClick={event => {
            event.stopPropagation();
            toggle();
          }}
        >
          +{hiddenCount}
        </button>
      )}
      {panel}
    </span>
  );
}
